import { Checkbox, TableCellProps, TableRow, Theme } from '@mui/material';
import { groupBy } from 'lodash';
import { Fragment } from 'react';
import { EMPTY_VALUE } from 'shared/constants';
import {
  CheckboxOnChangeEvent,
  Column,
  TableRowOnClickEvent,
} from 'shared/types';
import { StyledDivider, StyledRowCell } from '../styles';
import { MustHaveIds, OptionalIds, TableProps } from '../types';

const renderCell = ({
  format,
  cell,
  renderer,
}: Partial<Column> & { cell: string | number }) => {
  const value = cell || EMPTY_VALUE;

  if (format) return format(value, 'PLN');
  if (renderer) return renderer(cell);

  return value;
};

type RenderRowsArgs = {
  rows: TableProps['rows'];
  columns: TableProps['columns'];
  theme: Theme;
  optionalIds: OptionalIds;
  mustHaveIds: MustHaveIds;
  handleRowClick: (
    e: TableRowOnClickEvent | CheckboxOnChangeEvent,
    rowId: string,
    isMustHave: boolean
  ) => void;
};

export const renderRows = ({
  handleRowClick,
  optionalIds,
  mustHaveIds,
  theme,
  rows,
  columns,
}: RenderRowsArgs) => {
  const groupedRows = groupBy(rows, 'touchpointName');

  return Object.entries(groupedRows).flatMap(([_, groupRows], index) => {
    const rowColor =
      index % 2 === 0
        ? theme.palette.tableBackground.main
        : theme.palette.common.white;

    return groupRows.map((row) => {
      const isMustHave = Boolean(row.isMustHave);

      const checked = isMustHave
        ? Boolean(mustHaveIds[row.id])
        : Boolean(optionalIds[row.id]);

      return (
        <TableRow
          key={row.id}
          onClick={(e) => handleRowClick(e, row.id, isMustHave)}
          sx={{ backgroundColor: rowColor }}
        >
          <StyledRowCell padding="checkbox">
            <Checkbox
              color="primary"
              checked={checked}
              onChange={(e) => handleRowClick(e, row.id, isMustHave)}
              onClick={(e) => e.stopPropagation()}
            />
          </StyledRowCell>

          {columns.map(({ key, dividerColumn, format, align, renderer }) => (
            <Fragment key={`${row.id}-${String(key)}`}>
              <StyledRowCell align={align as TableCellProps['align']}>
                {renderCell({
                  format,
                  cell: row[key],
                  renderer,
                })}
              </StyledRowCell>

              {dividerColumn && (
                <StyledRowCell sx={{ padding: 0 }}>
                  <StyledDivider orientation="vertical" />
                </StyledRowCell>
              )}
            </Fragment>
          ))}
        </TableRow>
      );
    });
  });
};
